/**
 * OAuth callback handler.
 *
 * Mounted at /auth/callback. Receives the authorization code from Cognito,
 * exchanges it for tokens, then redirects to the home page.
 */

import { LitElement, html, css } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { authService } from '../../services/auth/auth-service.js'
import { router } from '../../utils/router.js'

@customElement('auth-callback')
export class AuthCallback extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      background: var(--cairn-background-color, #f8fafc);
    }

    .callback-container {
      text-align: center;
      padding: var(--cairn-spacing-xl, 32px);
    }

    .spinner {
      width: 48px;
      height: 48px;
      border: 4px solid var(--cairn-border-color, #e2e8f0);
      border-top-color: var(--cairn-primary-color, #3b82f6);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
      margin: 0 auto var(--cairn-spacing-lg, 24px);
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .status {
      font-size: var(--cairn-text-size-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .error {
      color: var(--cairn-error-color, #dc2626);
    }

    .retry-button {
      margin-top: var(--cairn-spacing-md, 16px);
      background: var(--cairn-primary-color, #3b82f6);
      color: white;
      border: none;
      padding: var(--cairn-spacing-sm, 8px) var(--cairn-spacing-md, 16px);
      border-radius: var(--cairn-border-radius, 8px);
      cursor: pointer;
      font-size: var(--cairn-text-size-sm, 14px);
    }
  `

  @state()
  private error: string | null = null

  override connectedCallback(): void {
    super.connectedCallback()
    void this.handleAuth()
  }

  private async handleAuth(): Promise<void> {
    const params = new URLSearchParams(window.location.search)
    const success = await authService.handleCallback(params)

    if (success) {
      const pendingToken = sessionStorage.getItem('pendingInviteToken')
      if (pendingToken) {
        sessionStorage.removeItem('pendingInviteToken')
        router.navigate(`/teams/invite/${pendingToken}`)
      } else if (authService.needsSetup()) {
        router.navigate('/auth/setup')
      } else if (authService.needsEula()) {
        router.navigate('/auth/eula')
      } else {
        router.navigate('/')
      }
    } else {
      this.error = params.get('error_description') ?? 'Authentication failed. Please try again.'
    }
  }

  private handleRetry(): void {
    this.error = null
    router.navigate('/')
  }

  override render(): unknown {
    if (this.error) {
      return html`
        <div class="callback-container">
          <p class="status error">${this.error}</p>
          <button class="retry-button" @click=${this.handleRetry}>Return to app</button>
        </div>
      `
    }

    return html`
      <div class="callback-container">
        <div class="spinner"></div>
        <p class="status">Signing in...</p>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'auth-callback': AuthCallback
  }
}
